import React, { useState, useEffect } from "react";
import "./mobileHeader.css";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { useLicense } from "../../context/licenseContext";
import { useLocation_ } from "../../context/locationContext";
import HeaderSelect from "../HeaderSelect/HeaderSelect";
import Notifications from "../Notifications/Notifications";
import InventoryIcon from "../Icons/inventoyrIcon";
import ShowroomIcon from "../Icons/showroomIcon";
import SettingsIcon from "../Icons/settingsIcon";
import AdminIcon from "../Icons/adminIcon";
import CRMIcon from "../Icons/crmIcon";
import DocumentsIcon from "../Icons/documentsIcon";
import OrdersIcon from "../Icons/ordersIcon";

const MobileHeader = ({ userId, headerTitle, userProfile }) => {
    const navigate = useNavigate();
    const location = useLocation(); 
    const [isOpen, setIsOpen] = useState(false);   
    const { currentLicense, setCurrentLicense } = useLicense();
    const { currentLocation, setCurrentLocation, locations, refreshLocations } = useLocation_();
    const [licenses, setLicenses] = useState([]);
    const [title, setTitle] = useState(""); 
    const [myPermissions, setMyPermissions] = useState(() => {
        try {
            const saved = localStorage.getItem("currentPermissions");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });

    useEffect(() => {
        if (!userId) return;
        apiClient
            .get(`/user/get-user-license-info`, { headers: { id: userId } })
            .then((res) => {
                const user = res.data;
                setLicenses(user.licenses || []);
                if (user.licenses?.length > 0) {
                    let savedLicense = null;
                    try {
                        savedLicense = JSON.parse(localStorage.getItem("currentLicense"));
                    } catch {
                        localStorage.removeItem("currentLicense");
                    }
                    const def = savedLicense
                        ? user.licenses.find(l => l.licenseNo === savedLicense.licenseNo) || user.licenses[0]
                        : user.licenses[0];
                    setCurrentLicense(def);
                    setTitle(def.title);
                    localStorage.setItem("currentLicense", JSON.stringify(def));
                    refreshLocations(def.licenseNo);
                }
            })
            .catch(console.error);
    }, [refreshLocations, setCurrentLicense, userId]);

    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${currentLicense.licenseNo}`)
            .then((res) => {
                setMyPermissions(res.data);
                localStorage.setItem("currentPermissions", JSON.stringify(res.data));
            })
            .catch(() => setMyPermissions({ can_upload_coa: 1, can_view_showroom: 1 }));
    }, [currentLicense]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    const handleNav = (path) => {
        setIsOpen(false);
        navigate(path);
    };

    // License
    const licenseOptions = licenses.map((l) => ({ value: l.licenseNo, label: l.company }));
    const licenseValue = currentLicense
        ? { value: currentLicense.licenseNo, label: currentLicense.company }
        : null;

    const handleLicenseChange = (option) => {
        const selectedLicense = licenses.find((l) => l.licenseNo === option.value);
        if (selectedLicense) {
            setCurrentLicense(selectedLicense);
            setTitle(selectedLicense.title);
            localStorage.setItem("currentLicense", JSON.stringify(selectedLicense));
            refreshLocations(selectedLicense.licenseNo);
        }
    };

    // Location
    const isScoped = currentLicense?.location_scoped === 1;
    const allLabel = currentLicense?.company || "All Locations";
    const locationOptions = locations.length > 0
        ? [
            ...(isScoped ? [] : [{ value: "all", label: allLabel }]),
            ...locations.map((l) => ({ value: l.locationId, label: l.locationName })),
          ]
        : [];

    const locationValue = currentLocation
        ? { value: currentLocation.locationId, label: currentLocation.locationName }
        : isScoped && locations[0]
            ? { value: locations[0].locationId, label: locations[0].locationName }
            : locations.length > 0
                ? { value: "all", label: allLabel }
                : null;

    const handleLocationChange = (option) => {
        if (option.value === "all") {
            setCurrentLocation(null);
        } else {
            const loc = locations.find((l) => l.locationId === option.value);
            if (loc) setCurrentLocation(loc);
        }
    };

    const canSeeSettings =
        title &&
        (title.toLowerCase() === "owner" ||
            title.toLowerCase() === "manager" ||
            title.toLowerCase() === "admin");

    return (
        <div className="mobileHeader">
            {/* Fixed top bar */}
            <div className="mobileHeader-container">
                <div className="mobile-header-left">
                    <button className="menu-icon-mobile" onClick={() => setIsOpen(true)} aria-label="Open menu">
                        <div className="line"></div>
                        <div className="line"></div>
                        <div className="line"></div>
                    </button>
                    <img src="/DYC-icon-ColorWheel.png" alt="DYC Logo" className="mobile-header-logo-img" />
                    <span className="mobile-header-title">{headerTitle}</span>
                </div>
                <div className="mobile-header-right">
                    <Notifications />
                </div>
            </div>

            {/* Backdrop */}
            <div
                className={`mobile-nav-backdrop ${isOpen ? "open" : ""}`}
                onClick={() => setIsOpen(false)}
            />

            {/* Slide-in drawer from left */}
            <div className={`mobile-nav-drawer ${isOpen ? "open" : ""}`}>
                {/* Drawer header */}
                <div className="mobile-nav-drawer-header">
                    <div className="mobile-nav-header-left">
                        <img src="/DYC-icon-ColorWheel.png" alt="DYC Logo" className="mobile-header-logo-img" />
                        <span className="mobile-nav-brand">DYC Terminal</span>
                    </div>
                    <button className="mobile-nav-close" onClick={() => setIsOpen(false)} aria-label="Close menu">
                        ✕
                    </button>
                </div>

                {/* License selector — admin/superadmin only */}
                {(userProfile === "admin" || userProfile === "superadmin") && (
                    <div className="mobile-nav-context">
                        <HeaderSelect
                            label="License"
                            options={licenseOptions}
                            value={licenseValue}
                            onChange={handleLicenseChange}
                            placeholder="Select License"
                        />
                    </div>
                )}

                {/* Location selector */}
                {locationOptions.length > 0 && (
                    <div className="mobile-nav-context">
                        <HeaderSelect
                            label="Location"
                            options={locationOptions}
                            value={locationValue}
                            onChange={handleLocationChange}
                            searchable
                            searchPlaceholder="Search locations..."
                            placeholder={allLabel}
                        />
                    </div>
                )}

                {locations.length === 0 && (
                    <div className="mobile-nav-context">
                        <span className="mobile-nav-context-label">Location</span>
                        <button
                            className="mobile-nav-create-location"
                            onClick={() => { setIsOpen(false); navigate("/settings/company/locations?newLocation=true"); }}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            Create Location
                        </button>
                    </div>
                )}

                {/* Nav links */}
                <nav className="mobile-nav-links">
                    <button
                        className={`mobile-nav-item ${location.pathname === "/Dashboard" ? "active" : ""}`}
                        onClick={() => handleNav("/Dashboard")}
                    >
                        <span className="mobile-nav-icon"><InventoryIcon /></span>
                        <span>Inventory</span>
                    </button>

                    {myPermissions?.can_view_showroom !== 0 && (
                        <button
                            className={`mobile-nav-item ${location.pathname === "/showroom" ? "active" : ""}`}
                            onClick={() => handleNav("/showroom")}
                        >
                            <span className="mobile-nav-icon"><ShowroomIcon /></span>
                            <span>DYC Marketplace</span>
                        </button>
                    )}

                    {myPermissions?.can_manage_orders === 1 && (
                        <button
                            className={`mobile-nav-item ${location.pathname === "/orders" ? "active" : ""}`}
                            onClick={() => handleNav("/orders")}
                        >
                            <span className="mobile-nav-icon"><OrdersIcon /></span>
                            <span>Wholesale Orders</span>
                        </button>
                    )}

                    {myPermissions?.can_view_compliance_docs === 1 && (
                        <button
                            className={`mobile-nav-item ${location.pathname === "/compliance-documents" ? "active" : ""}`}
                            onClick={() => handleNav("/compliance-documents")}
                        >
                            <span className="mobile-nav-icon"><DocumentsIcon /></span>
                            <span>Documents</span>
                        </button>
                    )}

                    {(myPermissions?.can_view_lab_tests !== 0 || myPermissions?.can_order_tests !== 0) && (
                        <button
                            className={`mobile-nav-item ${location.pathname === "/LabOrders" ? "active" : ""}`}
                            onClick={() => handleNav("/LabOrders")}
                        >
                            <span className="mobile-nav-icon">
                                <svg fill="var(--icons)" height="20px" width="20px" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20 2h-8a1 1 0 0 0-1 1v10.382L7.105 21.553A3 3 0 0 0 9.789 26h12.422a3 3 0 0 0 2.684-4.447L21 13.382V3a1 1 0 0 0-1-1zm-7 1h6v2h-6V3zm6 10.382V17h-6v-3.618L11 9.382V7h10v2.382L19 13.382zM22.895 22.553A1 1 0 0 1 22.211 24H9.789a1 1 0 0 1-.894-1.447L11 19h10l2.105 3.107-.21.446z"/>
                                    <circle cx="13" cy="21" r="1"/>
                                    <circle cx="17" cy="22" r="1"/>
                                    <circle cx="15" cy="20" r="1"/>
                                </svg>
                            </span>
                            <span>Lab Orders</span>
                        </button>
                    )}

                    {canSeeSettings && (
                        <button
                            className={`mobile-nav-item ${location.pathname === "/settings" ? "active" : ""}`}
                            onClick={() => handleNav("/settings")}
                        >
                            <span className="mobile-nav-icon"><SettingsIcon /></span>
                            <span>Settings</span>
                        </button>
                    )}

                    {/* Sales Analytics — disabled until feature is complete
                    <button
                        className={`mobile-nav-item ${location.pathname === "/SalesAnalytics" ? "active" : ""}`}
                        onClick={() => handleNav("/SalesAnalytics")}
                    >
                        <span className="mobile-nav-icon"><SalesAnalyticsIcon /></span>
                        <span>Sales Analytics</span>
                    </button>
                    */}

                    {(userProfile === "admin" || userProfile === "superadmin") && (
                        <button
                            className={`mobile-nav-item ${location.pathname.startsWith("/CRMPanel") ? "active" : ""}`}
                            onClick={() => handleNav("/CRMPanel/outbound")}
                        >
                            <span className="mobile-nav-icon"><CRMIcon /></span>
                            <span>CRM Panel</span>
                        </button>
                    )}

                    {(userProfile === "admin" || userProfile === "superadmin") && (
                        <button
                            className={`mobile-nav-item ${location.pathname === "/AdminPanel" ? "active" : ""}`}
                            onClick={() => handleNav("/AdminPanel")}
                        >
                            <span className="mobile-nav-icon"><AdminIcon /></span>
                            <span>Admin Panel</span>
                        </button>
                    )}
                </nav>
            </div>
        </div>
    );
};

export default MobileHeader;
